// @flow

import React, { useState } from 'react';
import * as Yup from 'yup';
import styled from 'styled-components';
import { i18n } from 'shared/utils';
import * as Actions from 'main-app/store/Actions';
import SignInMutation from 'main-app/mutations/SignIn';
import TwoFactorAuthFlow from 'shared/components/auth/TwoFactorAuthFlow';
import { Formik, Form, FieldGroup } from 'shared/components/form';
import Page from 'main-app/components/Page';
import Logo from 'main-app/components/PageHeaderLogo/img/logo.svg';
import Button from 'shared/components/common/Button';
import { colors, fonts } from 'shared/styleguide';

const SSO_ERROR_MESSAGES = {
  sso_failed: 'SSO sign-in failed. Please try again or use email/password.',
  invalid_state: 'SSO session expired. Please try again.',
  no_company_assigned: 'Your account has not been assigned to a company. Contact your administrator.',
  invalid_company: 'The company assigned to your account could not be found. Contact your administrator.',
};

function getSSOError() {
  if (typeof window === 'undefined') return null;
  const params = new URLSearchParams(window.location.search);
  const error = params.get('error');
  return SSO_ERROR_MESSAGES[error] || null;
}

function isExpiredSession() {
  if (typeof window === 'undefined') return false;
  return new URLSearchParams(window.location.search).get('expired') === 'true';
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  min-height: 100vh;
  padding: 10vh 16px 24px 16px;
`;

const LogoWrapper = styled.div`
  margin-bottom: 32px;

  svg {
    width: 156px;
    height: 156px;
  }
`;

const FormCard = styled.div`
  width: 100%;
  max-width: 440px;
`;

const Header = styled.div`
  ${fonts.largeTitle};
  color: ${colors.azul};
  padding-bottom: 24px;
  text-align: center;
`;

const Divider = styled.div`
  border-bottom: 1px solid ${colors.lightBlueGrey};
  margin: 16px 0 40px 0;
`;

const SSODivider = styled.div`
  display: flex;
  align-items: center;
  margin: 24px 0;
  color: ${colors.charcoalGrey};
  ${fonts.smallRegular};

  &::before,
  &::after {
    content: '';
    flex: 1;
    border-bottom: 1px solid ${colors.lightBlueGrey};
  }

  &::before { margin-right: 12px; }
  &::after  { margin-left: 12px; }
`;

const SSOButton = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  width: 100%;
  padding: 12px;
  border: 1px solid ${colors.lightBlueGrey};
  border-radius: 4px;
  color: ${colors.charcoalGrey};
  ${fonts.bodyRegular};
  text-decoration: none;
  cursor: pointer;

  &:hover {
    background-color: ${colors.lightBlueGrey};
  }
`;

const SSOError = styled.div`
  ${fonts.smallRegular};
  color: #e53935;
  margin-bottom: 16px;
  padding: 10px 12px;
  background: #ffebee;
  border-radius: 4px;
`;

const ExpiredBanner = styled.div`
  ${fonts.smallRegular};
  color: #e65100;
  margin-bottom: 16px;
  padding: 10px 12px;
  background: #fff3e0;
  border: 1px solid #ffcc80;
  border-radius: 4px;
`;

type TwoFactorState =
  | { mode: 'setup', token: string }
  | { mode: 'login', token: string }
  | null;

export default () => {
  const ssoError = getSSOError();
  const expired = isExpiredSession();
  const [twoFactorState, setTwoFactorState] = useState<TwoFactorState>(null);

  const handleSignInSuccess = () => {
    window.location = '/';
  };

  return (
    <Page hideMenu noPadding hideToolbar>
      <Wrapper>
        <LogoWrapper>
          <Logo />
        </LogoWrapper>
        <FormCard>
          <Header>{i18n.t('Welcome Back')}</Header>
          {expired && (
            <ExpiredBanner>
              {i18n.t('Your session has expired due to inactivity. Please sign in again.')}
            </ExpiredBanner>
          )}
          {ssoError && <SSOError>{ssoError}</SSOError>}
          {twoFactorState ? (
            <TwoFactorAuthFlow
              mode={twoFactorState.mode}
              token={twoFactorState.token}
              onSuccess={handleSignInSuccess}
              onBack={() => setTwoFactorState(null)}
              onError={message => Actions.alertNotification(message, 'error')}
            />
          ) : (
            <>
              <Formik
                initialValues={{
                  email: '',
                  password: '',
                }}
                validationSchema={Yup.object().shape({
                  email: Yup.string().email('Invalid Email').required('Required'),
                  password: Yup.string().required('Required'),
                })}
                onSubmit={async (values, { setSubmitting }) => {
                  setSubmitting(true);
                  try {
                    const response = await SignInMutation.commit({
                      variables: {
                        input: {
                          email: values.email,
                          password: values.password,
                        },
                      },
                    });

                    const payload = response.signIn;

                    if (payload.requiresTwoFactorSetup && payload.setupToken) {
                      setTwoFactorState({
                        mode: 'setup',
                        token: payload.setupToken,
                      });
                      setSubmitting(false);
                      return;
                    }

                    if (payload.requiresTwoFactor && payload.twoFactorToken) {
                      setTwoFactorState({
                        mode: 'login',
                        token: payload.twoFactorToken,
                      });
                      setSubmitting(false);
                      return;
                    }

                    handleSignInSuccess();
                  } catch (e) {
                    setSubmitting(false);
                    Actions.alertNotification(e.message || e, 'error');
                  }
                }}
                render={({ errors, isValid, isSubmitting, handleSubmit }) => (
                  <Form>
                    <FieldGroup
                      label={i18n.t('Email Address')}
                      name="email"
                      type="email"
                      placeholder={i18n.t('johndoe@example.com')}
                      error={errors.email}
                    />
                    <FieldGroup
                      label={i18n.t('Password')}
                      name="password"
                      type="password"
                      placeholder="Password"
                      error={errors.password}
                    />
                    <Divider />
                    <Button
                      type="submit"
                      disabled={!isValid}
                      loading={isSubmitting}
                      onClick={handleSubmit}
                    >
                      {i18n.t('Sign In')}
                    </Button>
                  </Form>
                )}
              />
              <SSODivider>or</SSODivider>
              <SSOButton href={expired ? '/oauth/keycloak/start?prompt=login' : '/oauth/keycloak/start'}>
                {i18n.t('SSO Login')}
              </SSOButton>
            </>
          )}
        </FormCard>
      </Wrapper>
    </Page>
  );
};
